import React from 'react';
import type { Path, UseFormSetValue } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import Tabs from '@/app/components/Tabs';
import { Input } from '@/app/components/form/Input';
import type { CreateClientFormData, EditClientResponse } from '@/types/clientsApiTypes';
import { cn } from '@/utils/сn';
import { RELATED_DATA_KEYS, type RelatedDataKeys } from '../../hooks/useUpdateTravelPreferencesWithRelatedData/types';

type InputIntent = 'search' | 'secondary' | 'primary' | 'old' | 'primaryWhite' | 'otp' | 'payment';

type ComfortDetailsPropsType = {
  fieldPrefix: `primary_passenger.preference_data` | `related_passengers.${number}.preference_data`;
  helperText?: string;
  className?: string;
  intent?: InputIntent;
  tabsIntent?: 'default' | 'third';
  mobileHeaderLayout?: 'inline' | 'stacked';
  onUpdateTravelPreferencesWithRelatedData?: (
    fieldKey: RelatedDataKeys,
    fieldName: string,
    fieldValue: unknown,
    fullPath: string
  ) => Promise<EditClientResponse | undefined>;
};

const sanitizeInteger = (value: string): string => {
  return value.replace(/\D+/g, '');
};

const useIntegerHandlers = (setValue: UseFormSetValue<CreateClientFormData>) => {
  return (name: Path<CreateClientFormData>) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(name, sanitizeInteger(e.target.value));
  };
};

export const ComfortDetails = ({
  fieldPrefix,
  className,
  intent,
  tabsIntent = 'third',
  mobileHeaderLayout = 'inline',
  helperText = 'Used only when helpful for specific adventure activities (e.g., required for African safaris, certain small airlines/aircraft, biking excursions, scuba diving, etc.)',
  onUpdateTravelPreferencesWithRelatedData,
}: ComfortDetailsPropsType) => {
  const { watch, setValue } = useFormContext<CreateClientFormData>();
  const watchString = (name: Path<CreateClientFormData>, fallback = '') => (watch(name) as string) || fallback;
  const system = watch(`${fieldPrefix}.personal.personal_metric_system` as Path<CreateClientFormData>);

  const usHeightName = `${fieldPrefix}.personal.comfort_related.us.height` as Path<CreateClientFormData>;
  const usWeightName = `${fieldPrefix}.personal.comfort_related.us.weight` as Path<CreateClientFormData>;

  const metricHeightName = `${fieldPrefix}.personal.comfort_related.metric.height` as Path<CreateClientFormData>;
  const metricWeightName = `${fieldPrefix}.personal.comfort_related.metric.weight` as Path<CreateClientFormData>;

  // Get values
  const usHeightRaw: string = watchString(usHeightName);
  const usWeight: string = watchString(usWeightName);

  const metricHeight: string = watchString(metricHeightName);
  const metricWeight: string = watchString(metricWeightName);

  // Parse US height (format: "6*72" = 6ft 72in)
  const [usHeightFt, usHeightIn] = usHeightRaw ? usHeightRaw.split('*') : ['', ''];

  const onIntChange = useIntegerHandlers(setValue);

  // Handlers for US height fields (combine ft*in)
  const handleUsHeightFtChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const ft = sanitizeInteger(e.target.value);
    const combined = ft && usHeightIn ? `${ft}*${usHeightIn}` : ft ? `${ft}*` : usHeightIn ? `*${usHeightIn}` : '';
    setValue(usHeightName, combined);
  };

  const handleUsHeightInChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inches = sanitizeInteger(e.target.value);
    const combined =
      usHeightFt && inches ? `${usHeightFt}*${inches}` : usHeightFt ? `${usHeightFt}*` : inches ? `*${inches}` : '';
    setValue(usHeightName, combined);
  };

  const handleUsHeightBlur = () => {
    const combined =
      usHeightFt && usHeightIn
        ? `${usHeightFt}*${usHeightIn}`
        : usHeightFt
          ? `${usHeightFt}*`
          : usHeightIn
            ? `*${usHeightIn}`
            : '';
    onUpdateTravelPreferencesWithRelatedData?.(
      RELATED_DATA_KEYS.personal,
      'us.height',
      combined,
      `${fieldPrefix}.personal.comfort_related.us.height`
    );
  };

  const tabs = [
    { id: 'us', label: 'US' },
    { id: 'metric', label: 'Metric' },
  ];

  const isStackedOnMobile = mobileHeaderLayout === 'stacked';

  return (
    <div className={cn('rounded-lg bg-white', className)}>
      <div
        className={cn(
          'mb-3 flex items-center justify-between',
          isStackedOnMobile && 'max-md:flex-col max-md:items-stretch max-md:gap-3'
        )}
      >
        <p
          className={cn(
            'text-black font-semibold font-inter text-lg leading-normal',
            isStackedOnMobile && 'max-md:text-base max-md:leading-6'
          )}
        >
          Comfort-related Details
        </p>

        <Tabs
          tabs={tabs}
          activeTab={system}
          onTabChange={(id) =>
            setValue(`${fieldPrefix}.personal.personal_metric_system` as Path<CreateClientFormData>, id)
          }
          intent={tabsIntent}
          className={cn(
            isStackedOnMobile &&
              'max-md:w-full max-md:gap-x-0 max-md:[&>span]:min-w-0 max-md:[&>span]:flex-1 max-md:[&>span>button]:w-full max-md:[&>span>button]:text-center'
          )}
        />
      </div>

      {system === 'metric' && (
        <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1">
          <Input
            name={metricHeightName}
            label="Height"
            suffix="cm"
            placeholder="—"
            value={metricHeight || ''}
            onChange={onIntChange(metricHeightName)}
            onBlur={() =>
              onUpdateTravelPreferencesWithRelatedData?.(
                RELATED_DATA_KEYS.personal,
                'metric.height',
                metricHeight,
                `${fieldPrefix}.personal.comfort_related.metric.height`
              )
            }
            intent={intent}
          />

          <Input
            name={metricWeightName}
            label="Weight"
            suffix="kg"
            placeholder="—"
            value={metricWeight || ''}
            onChange={onIntChange(metricWeightName)}
            onBlur={() =>
              onUpdateTravelPreferencesWithRelatedData?.(
                RELATED_DATA_KEYS.personal,
                'metric.weight',
                metricWeight,
                `${fieldPrefix}.personal.comfort_related.metric.weight`
              )
            }
            intent={intent}
          />
        </div>
      )}

      {system === 'us' && (
        <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1">
          <div className="flex flex-col gap-2">
            <label className="text-body-small text-blue-900">Height</label>

            <div className="grid grid-cols-2 gap-2">
              <Input
                name={`${usHeightName}_ft`}
                suffix="ft"
                placeholder="—"
                value={usHeightFt || ''}
                onChange={handleUsHeightFtChange}
                onBlur={handleUsHeightBlur}
                intent={intent}
              />

              <Input
                name={`${usHeightName}_in`}
                suffix="in"
                placeholder="—"
                value={usHeightIn || ''}
                onChange={handleUsHeightInChange}
                onBlur={handleUsHeightBlur}
                intent={intent}
              />
            </div>
          </div>

          <Input
            name={usWeightName}
            label="Weight"
            placeholder="—"
            suffix="lb"
            value={usWeight || ''}
            onChange={onIntChange(usWeightName)}
            onBlur={() =>
              onUpdateTravelPreferencesWithRelatedData?.(
                RELATED_DATA_KEYS.personal,
                'us.weight',
                usWeight,
                `${fieldPrefix}.personal.comfort_related.us.weight`
              )
            }
            intent={intent}
          />
        </div>
      )}

      <p className="mt-2 text-body-small text-grey-700">{helperText}</p>
    </div>
  );
};
